import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useInView } from '../../hooks/useInView';

const CompanyPage = () => {
  const { ref: heroRef, isInView: heroInView } = useInView({ threshold: 0.3 });
  const { ref: missionRef, isInView: missionInView } = useInView({ threshold: 0.2 });
  const { ref: valuesRef, isInView: valuesInView } = useInView({ threshold: 0.2 });
  const { ref: infoRef, isInView: infoInView } = useInView({ threshold: 0.2 });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sinax.co.jp';
    
    const aboutPageSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "会社案内 | 株式会社SINAX",
      "description": "株式会社SINAXの会社概要、ミッション、価値観をご紹介します。",
      "url": `${siteUrl}/company`,
      "mainEntity": {
        "@type": "Organization",
        "name": "株式会社SINAX",
        "foundingDate": "2025-12",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "神田須田町1丁目7番8号 VORT秋葉原Ⅳ 2F",
          "addressLocality": "千代田区",
          "addressRegion": "東京都",
          "postalCode": "101-0041",
          "addressCountry": "JP"
        },
        "founder": [
          {
            "@type": "Person",
            "name": "村永 裕一",
            "jobTitle": "代表取締役"
          },
          {
            "@type": "Person",
            "name": "木戸 佑哉",
            "jobTitle": "取締役"
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(aboutPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const companyInfo = [
    { label: '会社名', value: '株式会社SINAX' },
    { label: '設立', value: '2025年12月' },
    {
      label: '所在地',
      value: '〒101-0041\n東京都千代田区神田須田町1丁目7番8号\nVORT秋葉原Ⅳ 2F',
    },
    { label: '代表取締役', value: '村永 裕一' },
    { label: '取締役', value: '木戸 佑哉' },
    { label: '事業内容', value: 'SES事業、ITコンサルティング、アプリ開発' },
    { label: '資本金', value: '100万円' },
  ];

  const values = [
    {
      number: '01',
      title: '繋がり',
      subtitle: 'Connection',
      description: '人と人、想いと想いの間に立ち、縁を紡ぐ。',
    },
    {
      number: '02',
      title: '柔軟性',
      subtitle: 'Flexibility',
      description: '変化を恐れず、流れの中で新しい形を見つける。',
    },
    {
      number: '03',
      title: '誠実',
      subtitle: 'Integrity',
      description: '相手を思い、正直であることを軸に行動する。',
    },
    {
      number: '04',
      title: '調和',
      subtitle: 'Harmony',
      description: '多様な個が共に在り、ひとつの和を生み出す。',
    },
  ];

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section ref={heroRef} className="py-20 lg:py-28 bg-white">
          <div className="px-8 lg:px-16 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <p className="font-sans text-xs text-warmgray tracking-[0.3em] mb-4 uppercase">Company</p>
              <h1 className="font-serif text-5xl lg:text-6xl text-darkgray font-bold mb-8">会社案内</h1>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section ref={missionRef} className="py-20 lg:py-28 bg-lightgray">
          <div className="px-8 lg:px-16 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={missionInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <p className="font-sans text-xs text-warmgray tracking-[0.3em] mb-6 uppercase">Our Mission</p>
                <h2 className="font-serif text-4xl lg:text-5xl text-darkgray font-bold leading-relaxed mb-6">
                  共に歩み、<br />
                  　共に未来の形を作る。<br />
                  縁を結び、人で生きる。
                </h2>
                <p className="font-sans text-base text-gray-600 leading-loose">
                  人と人との縁を大切にし、ITの力で現場と未来をつなぐ。<br />
                  私たちは、お客様と共に歩み、共に成長していくことが使命としています。
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={missionInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://static.readdy.ai/image/4df5c7deddaddbbeeb26e7a7cacf2afd/5c890f8d98210512507b4cc1f208f94b.jpeg"
                  alt="Mission"
                  className="w-full h-[400px] object-cover rounded-2xl shadow-sm"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section ref={valuesRef} className="py-20 lg:py-28 bg-white">
          <div className="px-8 lg:px-16 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <p className="font-sans text-xs text-warmgray tracking-[0.3em] mb-4 uppercase">Our Values</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-darkgray font-bold">SINAXの価値観</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.number}
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.1 * index }}
                  className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300 relative"
                >
                  <div className="absolute top-6 left-6 w-12 h-12 rounded-full bg-lightgray flex items-center justify-center">
                    <span className="font-serif text-sm text-sage font-medium">{value.number}</span>
                  </div>
                  <div className="mt-16">
                    <h3 className="font-serif text-2xl text-darkgray font-bold mb-2">{value.title}</h3>
                    <p className="font-sans text-xs text-warmgray tracking-wider mb-4 uppercase">{value.subtitle}</p>
                    <p className="font-sans text-sm text-gray-600 leading-loose">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Info Section */}
        <section ref={infoRef} className="py-20 lg:py-28 bg-lightgray relative overflow-hidden">
          {/* 背景ロゴ */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none">
            <img 
              src="https://static.readdy.ai/image/4df5c7deddaddbbeeb26e7a7cacf2afd/04612241165ac6755bde3331d7ab7803.png"
              alt="SINAX Logo Background"
              className="w-[500px] h-[500px] lg:w-[700px] lg:h-[700px] object-contain"
            />
          </div>

          <div className="px-8 lg:px-16 max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={infoInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="mb-16"
            >
              <p className="font-sans text-xs text-warmgray tracking-[0.3em] mb-4 uppercase">Company Info</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-darkgray font-bold">会社概要</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={infoInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="space-y-6">
                {companyInfo.map((info, index) => (
                  <div
                    key={info.label}
                    className="pb-6 border-b border-gray-200 last:border-b-0"
                  >
                    <dt className="font-sans text-sm text-warmgray mb-2 tracking-wide">{info.label}</dt>
                    <dd className="font-sans text-base lg:text-lg text-darkgray leading-relaxed font-medium whitespace-pre-line">
                      {info.value}
                    </dd>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CompanyPage;